import React, { useCallback, useState } from 'react';
import { Modal, Popover } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import AudioRecorderPanel from './AudioRecorderPanel';
import { useAudioRecorder } from '../../hooks/useAudioRecorder';

type PopoverPlacement =
	| 'top'
	| 'left'
	| 'right'
	| 'bottom'
	| 'topLeft'
	| 'topRight'
	| 'bottomLeft'
	| 'bottomRight'
	| 'leftTop'
	| 'leftBottom'
	| 'rightTop'
	| 'rightBottom';

interface AudioRecorderProps {
	containerType?: 'popover' | 'modal';
	onSend: (blob: Blob, file: File) => void | Promise<void>;
	onCancel?: () => void;
	popoverPlacement?: PopoverPlacement;
	modalTitle?: string;
	modalWidth?: number;
	children: React.ReactNode;
	disabled?: boolean;
	sendLabel?: string;
}

const AudioRecorder: React.FC<AudioRecorderProps> = ({
	containerType = 'modal',
	onSend,
	onCancel,
	popoverPlacement = 'top',
	modalTitle = 'Starting recording',
	modalWidth = 400,
	children,
	disabled,
	sendLabel,
}) => {
	const [visible, setVisible] = useState(false);
	const [uploading, setUploading] = useState(false);
	const recorder = useAudioRecorder();
	const { startRecording, deleteRecording } = recorder;

	const handleOpen = useCallback(() => {
		if (disabled) {
			return;
		}
		setVisible(true);
		startRecording();
	}, [disabled, startRecording]);

	const handleClose = useCallback(() => {
		if (uploading) {
			return;
		}
		deleteRecording();
		setVisible(false);
		if (onCancel) {
			onCancel();
		}
	}, [uploading, deleteRecording, onCancel]);

	const handleSend = useCallback(
		(blob: Blob, file: File) => {
			setUploading(true);
			const result = onSend(blob, file);
			if (result && typeof (result as any).then === 'function') {
				(result as Promise<void>)
					.then(() => {
						setUploading(false);
						setVisible(false);
						deleteRecording();
					})
					.catch(() => {
						setUploading(false);
					});
			} else {
				setUploading(false);
				setVisible(false);
				deleteRecording();
			}
		},
		[onSend, deleteRecording]
	);

	const handlePopoverTriggerClick = useCallback(() => {
		if (!visible) {
			handleOpen();
		}
	}, [visible, handleOpen]);

	const panelContent = (
		<AudioRecorderPanel
			recorder={recorder}
			onSend={handleSend}
			onDelete={handleClose}
			uploading={uploading}
			sendLabel={sendLabel}
		/>
	);

	if (containerType === 'popover') {
		const popoverTitle = (
			<div className="flex items-center justify-between">
				<span>{modalTitle}</span>
				{!uploading && (
					<button
						type="button"
						onClick={handleClose}
						className="flex h-6 w-6 cursor-pointer items-center justify-center rounded border-none bg-transparent p-0 text-gray-400 hover:text-gray-600"
						aria-label="Cancel recording"
					>
						<CloseOutlined style={{ fontSize: 12 }} />
					</button>
				)}
			</div>
		);

		return (
			<>
				{visible && (
					<div
						className="fixed inset-0"
						style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 1000 }}
						onClick={undefined}
					/>
				)}
				<Popover
					visible={visible}
					content={panelContent}
					trigger="click"
					placement={popoverPlacement}
					destroyTooltipOnHide
					onVisibleChange={undefined}
					title={popoverTitle}
					zIndex={1001}
				>
					<span onClick={handlePopoverTriggerClick}>{children}</span>
				</Popover>
			</>
		);
	}

	return (
		<>
			<span onClick={handleOpen}>{children}</span>
			<Modal
				visible={visible}
				footer={null}
				closable={!uploading}
				onCancel={handleClose}
				destroyOnClose
				width={modalWidth}
				title={modalTitle}
				maskClosable={!uploading}
			>
				{panelContent}
			</Modal>
		</>
	);
};

export default AudioRecorder;
